/**
 * QuestionProgressIndicator component
 * Displays current question number and attempt count for serverless sessions
 * Shown in the session header for real-time progress tracking
 */

import { Badge } from '@/components/ui/badge';
import type { QuestionProgress } from '@/types/session';

interface QuestionProgressIndicatorProps {
  /** Question progress data from serverless response */
  questionProgress?: QuestionProgress;
}

/**
 * Compact progress indicator for serverless sessions
 * Shows "Q1/2 Attempt 2/5" format
 * Renders nothing if questionProgress is undefined (MCP sessions or not yet loaded)
 */
export function QuestionProgressIndicator({ questionProgress }: QuestionProgressIndicatorProps) {
  // Hide component if no progress data (MCP sessions don't have this data)
  if (!questionProgress) {
    return null;
  }

  const { currentQuestion, totalQuestions, attemptsThisQuestion, maxAttempts, phase } =
    questionProgress;

  // GAP-C (C-9): in the restate phase the attempt badge is REPLACED, not supplemented.
  //
  // Two reasons, and the second is the one that matters. Mechanically,
  // `attemptsThisQuestion` is frozen at the scaffold cap for the whole phase, so the
  // badge would sit at a stuck "Attempt 6/6" for the duration. And pedagogically,
  // nothing should be counting down while a child is saying an answer back: the
  // restate phase exists to remove exactly the pressure a countdown reintroduces.
  //
  // `restateAttemptsRemaining` is deliberately NOT rendered. The count is real and it
  // governs the exit; it is simply not the child's to watch.
  const isRestating = phase === 'restate';

  // BUG-20260227-181201: Cap display at totalQuestions — backend advances
  // currentQuestionIndex past the last question on completion, causing Q3/2.
  const displayQuestion = Math.min(currentQuestion, totalQuestions);

  return (
    <div className="flex items-center gap-2 text-sm">
      {/* Question number — warm pill */}
      <span className="inline-flex items-center rounded-full border border-otter-sand bg-white px-2.5 py-0.5 font-display font-semibold text-otter-earth-800 shadow-sm">
        Q{displayQuestion}/{totalQuestions}
      </span>

      {/* Decorative segmented progress dots (filled = done, glowing = current) */}
      {totalQuestions > 0 && totalQuestions <= 12 && (
        <div className="hidden items-center gap-1 sm:flex" aria-hidden="true">
          {Array.from({ length: totalQuestions }).map((_, i) => {
            const done = i < displayQuestion - 1;
            const current = i === displayQuestion - 1;
            return (
              <span
                key={i}
                className={
                  done
                    ? 'h-1.5 w-3.5 rounded-full bg-gradient-to-r from-otter-orange-400 to-otter-orange-500'
                    : current
                      ? 'h-1.5 w-3.5 rounded-full bg-otter-orange-400 ring-2 ring-otter-orange-400/25'
                      : 'h-1.5 w-3.5 rounded-full bg-otter-sand'
                }
              />
            );
          })}
        </div>
      )}

      {/* Attempt count badge — replaced entirely during the restate phase, so nothing
          counts down while the child is saying the answer back. */}
      {isRestating ? (
        <Badge
          variant="outline"
          className="border-pebbles-300 bg-pebbles-50 text-xs font-semibold text-pebbles-700"
        >
          In your own words
        </Badge>
      ) : (
        <Badge
          variant="outline"
          className="border-otter-orange-300 bg-otter-orange-50 text-xs font-semibold text-otter-orange-700"
        >
          Attempt {attemptsThisQuestion}/{maxAttempts}
        </Badge>
      )}
    </div>
  );
}
